import {
    X,
    ArrowUpRight,
    ArrowDownLeft,
    Package,
    User,
    Mail,
    FileText,
    Trash2,
    CheckCircle2,
    RotateCw,
    AlertCircle,
    Calendar,
    Link2,
    Paperclip,
    ExternalLink,
    Printer,
    ShieldAlert,
    ShieldCheck,
    Pencil,
    Save,
} from 'lucide-react';
import { MasterBOLModal } from './loads/MasterBOLModal';
import { openDocumentView } from '../utils/openDocumentView';
import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuthStore } from '../hooks/useAuth';
import { isOperationsTierUser } from './entitlements/OperationsFeatureGate';
import { useUIStore } from '../hooks/useUIStore';
import {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogContent,
} from './ui/dialog';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogAction,
    AlertDialogCancel,
} from './ui/alert-dialog';

interface PO {
    id: string;
    customerPoNumber: string;
    clientName: string;
    type: 'inbound' | 'outbound';
    status: 'open' | 'closed' | 'on_hold';
    totalSkids?: number;
    totalWeight?: number;
    carrierEmail?: string;
    description?: string;
    createdAt?: string;
    // Hold audit fields
    holdReason?: string;
    holdNotes?: string;
    holdSetAt?: string;
    holdSetBy?: string;
    holdReleasedAt?: string;
    holdReleasedBy?: string;
    holdReleaseReason?: string;
    // Phase 3: Shortage Resolution
    parentPoId?: string | null;
    resolutionStatus?: string | null;
    followupNumber?: number | null;
    // V1.3A: Arrival Requirement
    dueDate?: string | null;
    priority?: 'standard' | 'urgent' | 'critical';
}

interface PODetailsDrawerProps {
    po: PO | null;
    open: boolean;
    onClose: () => void;
    onUpdate: (updatedPo?: PO) => void; // Refresh SWR & active drawer PO
    onRequestHold?: (po: PO) => void;
    onRequestReleaseHold?: (po: PO) => void;
}

export function extractPoCalendarYmd(dueDate?: string | null): string {
    if (!dueDate) return '';
    const trimmed = String(dueDate).trim();
    const ymdMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (ymdMatch && !trimmed.endsWith(':59.999Z')) {
        return ymdMatch[1];
    }
    const d = new Date(trimmed);
    if (isNaN(d.getTime())) return ymdMatch ? ymdMatch[1] : '';
    return d.toISOString().slice(0, 10);
}

export function formatPoCalendarDate(dueDate?: string | null): string {
    const ymd = extractPoCalendarYmd(dueDate);
    if (!ymd) return '—';
    const d = new Date(`${ymd}T12:00:00.000Z`);
    if (isNaN(d.getTime())) return ymd;
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    });
}

export function PODetailsDrawer({ po, open, onClose, onUpdate, onRequestHold, onRequestReleaseHold }: PODetailsDrawerProps) {
    const { token, user } = useAuthStore();
    const isOperationsEntitled = isOperationsTierUser(user);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showBOL, setShowBOL] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(false);

    // Edit mode state
    const [isEditing, setIsEditing] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editFields, setEditFields] = useState({
        customerPoNumber: '',
        clientName: '',
        type: 'inbound' as 'inbound' | 'outbound',
        totalSkids: '',
        totalWeight: '',
        carrierEmail: '',
        description: '',
        dueDate: '',
        priority: 'standard' as 'standard' | 'urgent' | 'critical',
    });
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Reset edit state when PO changes or drawer closes
    useEffect(() => {
        if (!open) {
            setIsEditing(false);
            setFieldErrors({});
        }
    }, [open]);

    const startEditing = () => {
        if (!po) return;
        setEditFields({
            customerPoNumber: po.customerPoNumber || '',
            clientName: po.clientName || '',
            type: po.type || 'inbound',
            totalSkids: po.totalSkids?.toString() || '',
            totalWeight: po.totalWeight?.toString() || '',
            carrierEmail: po.carrierEmail || '',
            description: po.description || '',
            dueDate: extractPoCalendarYmd(po.dueDate),
            priority: po.priority || 'standard',
        });
        setFieldErrors({});
        setError(null);
        setIsEditing(true);
    };

    const handleEditSave = async () => {
        if (!po || !token) return;

        // Client-side validation
        const clientErrors: Record<string, string> = {};
        if (!editFields.customerPoNumber.trim()) clientErrors.customerPoNumber = 'PO Number is required';
        if (!editFields.clientName.trim()) clientErrors.clientName = 'Client Name is required';
        if (editFields.totalSkids && (isNaN(Number(editFields.totalSkids)) || Number(editFields.totalSkids) < 0)) {
            clientErrors.totalSkids = 'Must be a non-negative number';
        }
        if (editFields.totalWeight && (isNaN(Number(editFields.totalWeight)) || Number(editFields.totalWeight) < 0)) {
            clientErrors.totalWeight = 'Must be a non-negative number';
        }
        if (Object.keys(clientErrors).length > 0) {
            setFieldErrors(clientErrors);
            return;
        }

        setEditSaving(true);
        setFieldErrors({});
        setError(null);
        try {
            const res = await fetch(`/api/warehouse-pos/${po.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    customerPoNumber: editFields.customerPoNumber.trim(),
                    clientName: editFields.clientName.trim(),
                    type: editFields.type,
                    totalSkids: editFields.totalSkids === '' ? 0 : Number(editFields.totalSkids),
                    totalWeight: editFields.totalWeight === '' ? 0 : Number(editFields.totalWeight),
                    carrierEmail: editFields.carrierEmail.trim(),
                    description: editFields.description.trim(),
                    dueDate: editFields.dueDate || null,
                    priority: editFields.priority,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                if (data.fieldErrors) {
                    setFieldErrors(data.fieldErrors);
                }
                throw new Error(data.error || 'Failed to save changes');
            }
            setIsEditing(false);
            onUpdate(data.po);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setEditSaving(false);
        }
    };

    // PO document state
    const [poDocuments, setPoDocuments] = useState<any[]>([]);
    const [poDocsLoading, setPoDocsLoading] = useState(false);
    const [uploadingPODoc, setUploadingPODoc] = useState(false);
    const [poDocsError, setPoDocsError] = useState<string | null>(null);

    // Auto-close on route change (prevents stuck overlay)
    const [location] = useLocation();
    const prevLocationRef = useRef(location);

    useEffect(() => {
        if (prevLocationRef.current !== location) {
            prevLocationRef.current = location;
            if (open) {
                onClose();
            }
        }
    }, [location, open, onClose]);

    // Fetch PO documents when drawer opens
    useEffect(() => {
        if (!open || !po || !token) {
            setPoDocuments([]);
            setPoDocsError(null);
            return;
        }
        let cancelled = false;
        setPoDocsLoading(true);
        setPoDocsError(null);
        fetch(`/api/warehouse-pos/${po.id}/documents`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`${r.status}`)))
            .then(d => { if (!cancelled) setPoDocuments(d?.documents ?? []); })
            .catch(() => { if (!cancelled) setPoDocsError('Failed to load documents'); })
            .finally(() => { if (!cancelled) setPoDocsLoading(false); });
        return () => { cancelled = true; };
    }, [open, po, token]);

    const handlePODocUpload = async (e: React.ChangeEvent<HTMLInputElement>, fileType: string) => {
        const file = e.target.files?.[0];
        if (!file || !po || !token) return;
        e.target.value = '';
        setUploadingPODoc(true);
        setPoDocsError(null);
        try {
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => reject(new Error('Failed to read file'));
                reader.readAsDataURL(file);
            });
            const res = await fetch(`/api/warehouse-pos/${po.id}/documents`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ fileName: file.name, fileUrl: base64, fileType }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({ error: 'Upload failed' }));
                throw new Error(d.error || `Upload failed (${res.status})`);
            }
            const d = await res.json();
            setPoDocuments(prev => [d.document, ...prev]);
        } catch (err: any) {
            setPoDocsError(err?.message || 'Upload failed');
        } finally {
            setUploadingPODoc(false);
        }
    };

    const handleStatusChange = async (newStatus: 'open' | 'closed' | 'on_hold', holdReason?: string) => {
        if (!po) return;
        setLoading(true);
        setError(null);
        try {
            const body: Record<string, string> = { status: newStatus };
            if (holdReason) body.holdReason = holdReason;

            const res = await fetch(`/api/warehouse-pos/${po.id}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                if (data.error === 'PO_RECEIPTS_REQUIRED_BEFORE_COMPLETION') {
                    throw new Error('Receiving results are required before completing this PO. Please record unloading results for linked appointments first.');
                }
                throw new Error(data.message || data.error || 'Failed to update status');
            }

            onUpdate();
            onClose();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!po) return;
        setPendingDelete(false);

        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`/api/warehouse-pos/${po.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.message || 'Failed to delete PO');
            }

            onUpdate();
            onClose();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Safe guard: if not open, render nothing (though Sheet handles this, it's safer for props access)
    if (!po && open) return null;

    return (<>
        <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
            {po && (
                <DialogPortal>
                    <DialogOverlay />
                    <DialogContent
                        className="w-[90vw] max-w-[600px] max-h-[85vh] p-0 flex flex-col gap-0"
                    >
                        <div className="flex-none p-5 border-b border-slate-200 dark:border-white/5 bg-white dark:bg-slate-950/95 backdrop-blur z-10 rounded-t-xl">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${po.type === 'inbound'
                                        ? 'text-emerald-700 border-emerald-200 bg-emerald-50 dark:text-emerald-400 dark:border-emerald-500/30 dark:bg-emerald-500/10'
                                        : 'text-blue-700 border-blue-200 bg-blue-50 dark:text-blue-400 dark:border-blue-500/30 dark:bg-blue-500/10'
                                        }`}>
                                        {po.type === 'inbound' ? <ArrowDownLeft size={11} /> : <ArrowUpRight size={11} />}
                                        {po.type}
                                    </div>
                                    <div className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${po.status === 'open'
                                        ? 'text-blue-700 border-blue-200 bg-blue-50 dark:text-blue-400 dark:border-blue-500/30 dark:bg-blue-500/10'
                                        : po.status === 'on_hold'
                                            ? 'text-red-700 border-red-200 bg-red-50 dark:text-red-400 dark:border-red-500/30 dark:bg-red-500/10'
                                            : 'text-emerald-700 border-emerald-200 bg-emerald-50 dark:text-emerald-400 dark:border-emerald-500/30 dark:bg-emerald-500/10'
                                        }`}>
                                        {po.status === 'on_hold' ? 'On Hold' : po.status}
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 -mr-2 text-slate-400 hover:text-foreground hover:bg-slate-100 dark:hover:bg-white/10 rounded-full transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="text-left space-y-1">
                                <h2 className="text-xl font-mono font-bold text-foreground tracking-tight">
                                    {po.customerPoNumber}
                                </h2>
                                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Calendar size={14} />
                                    Created on {po.createdAt ? new Date(po.createdAt).toLocaleDateString() : 'Unknown Date'}
                                </p>
                            </div>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-5 space-y-6">
                            {error && (
                                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
                                    <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
                                    <div className="space-y-1">
                                        <h4 className="text-sm font-medium text-red-200">Action Failed</h4>
                                        <p className="text-xs text-red-300/80">{error}</p>
                                    </div>
                                </div>
                            )}

                            {/* Hold Audit Section */}
                            {(po.status === 'on_hold' || po.holdReleasedAt) && (
                                <section className="space-y-3">
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-500 flex items-center gap-2">
                                        {po.status === 'on_hold' ? <ShieldAlert size={14} className="text-red-400" /> : <ShieldCheck size={14} className="text-emerald-400" />}
                                        {po.status === 'on_hold' ? 'Hold Status' : 'Hold History'}
                                    </h4>
                                    <div className={`rounded-xl p-4 border space-y-3 ${
                                        po.status === 'on_hold'
                                            ? 'bg-red-50 dark:bg-red-500/5 border-red-200 dark:border-red-500/20'
                                            : 'bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/5'
                                    }`}>
                                        {/* Hold info */}
                                        {po.holdReason && (
                                            <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-500 mb-0.5 block">Hold Reason</label>
                                                <p className="text-sm font-medium text-red-600 dark:text-red-400 uppercase tracking-wide">
                                                    {po.holdReason.replace(/_/g, ' ')}
                                                </p>
                                            </div>
                                        )}
                                        {po.holdNotes && (
                                            <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-500 mb-0.5 block">Notes</label>
                                                <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{po.holdNotes}</p>
                                            </div>
                                        )}
                                        {po.holdSetAt && (
                                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                                <span>Held {new Date(po.holdSetAt).toLocaleString()}</span>
                                            </div>
                                        )}

                                        {/* Release info — shown when hold was released */}
                                        {po.holdReleasedAt && (
                                            <div className="pt-3 mt-3 border-t border-slate-200 dark:border-white/10 space-y-2">
                                                <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
                                                    <ShieldCheck size={12} />
                                                    Released
                                                </div>
                                                {po.holdReleaseReason && (
                                                    <div>
                                                        <label className="text-xs text-slate-500 dark:text-slate-500 mb-0.5 block">Release Reason</label>
                                                        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{po.holdReleaseReason}</p>
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                                    <span>Released {new Date(po.holdReleasedAt).toLocaleString()}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            )}

                            {/* Client Info */}
                            <section className="space-y-3">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-500 flex items-center gap-2">
                                    <User size={14} /> Client Details
                                </h4>
                                <div className="bg-slate-50 dark:bg-white/5 rounded-xl p-4 border border-slate-200 dark:border-white/5 space-y-4">
                                    <div>
                                        <label className="text-xs text-slate-500 dark:text-slate-500 mb-1 block">Client Name</label>
                                        <p className="text-base font-medium text-foreground">{po.clientName}</p>
                                    </div>
                                    <div className="pt-3 border-t border-slate-100 dark:border-white/5">
                                        <label className="text-xs text-slate-500 dark:text-slate-500 mb-1 block">Carrier Email</label>
                                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                                            <Mail size={14} className="text-slate-500" />
                                            <span className="truncate">{po.carrierEmail || 'Not provided'}</span>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Arrival Requirement */}
                            {(po.dueDate || (po.priority && po.priority !== 'standard')) && (
                                <div className="space-y-3">
                                    <h4 className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-primary" /> Arrival Requirement
                                    </h4>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        {po.dueDate && (
                                            <div>
                                                <span className="text-xs text-slate-500 dark:text-slate-400 block mb-0.5">Due Date</span>
                                                <p className="font-semibold text-slate-800 dark:text-white">{formatPoCalendarDate(po.dueDate)}</p>
                                            </div>
                                        )}
                                        <div>
                                            <span className="text-xs text-slate-500 dark:text-slate-400 block mb-0.5">Priority</span>
                                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider ${
                                                po.priority === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                : po.priority === 'urgent' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                            }`}>{po.priority === 'critical' ? '🔴 Critical' : po.priority === 'urgent' ? '⚡ Urgent' : 'Standard'}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Logistics */}
                            <section className="space-y-3">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-500 flex items-center gap-2">
                                    <Package size={14} /> Cargo Logistics
                                </h4>
                                <div className="bg-slate-50 dark:bg-white/5 rounded-xl p-4 border border-slate-200 dark:border-white/5">
                                    <div className="grid grid-cols-2 gap-6">
                                        <div>
                                            <label className="text-xs text-slate-500 dark:text-slate-500 mb-1 block">Total Skids</label>
                                            <p className="text-3xl font-light text-foreground">{po.totalSkids || 0}</p>
                                        </div>
                                        <div>
                                            <label className="text-xs text-slate-500 mb-1 block">Est. Weight</label>
                                            <p className="text-3xl font-light text-foreground">{po.totalWeight ? `${po.totalWeight.toLocaleString()} lbs` : '--'}</p>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Notes */}
                            <section className="space-y-3">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-500 flex items-center gap-2">
                                    <FileText size={14} /> Notes
                                </h4>
                                <div className="bg-slate-50 dark:bg-white/5 rounded-xl p-4 border border-slate-200 dark:border-white/5 min-h-[100px]">
                                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                                        {po.description || 'No additional notes provided.'}
                                    </p>
                                </div>
                            </section>

                            {/* Phase 3: Shortage Resolution */}
                            {(po.resolutionStatus || po.parentPoId) && (
                                <section className="space-y-3">
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-500 flex items-center gap-2">
                                        <Link2 size={14} /> Shortage Resolution
                                    </h4>
                                    <div className="bg-slate-50 dark:bg-white/5 rounded-xl p-4 border border-slate-200 dark:border-white/5 space-y-3">
                                        {/* Lineage */}
                                        {po.parentPoId && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-muted-foreground">Follow-up of:</span>
                                                <span className="font-mono font-bold text-cyan-500">
                                                    {po.customerPoNumber.replace(/-R\d+$/, '')}
                                                </span>
                                                <span className="text-[10px] bg-cyan-500/10 text-cyan-400 px-1.5 py-0.5 rounded font-bold border border-cyan-500/20">
                                                    R{po.followupNumber || '?'}
                                                </span>
                                            </div>
                                        )}

                                        {/* Resolution status */}
                                        {po.resolutionStatus && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-muted-foreground">Resolution:</span>
                                                <span className={`font-medium ${
                                                    po.resolutionStatus === 'resolved' ? 'text-emerald-500'
                                                    : po.resolutionStatus === 'written_off' ? 'text-slate-500'
                                                    : po.resolutionStatus === 'unresolved' ? 'text-amber-500'
                                                    : 'text-blue-500'
                                                }`}>
                                                    {po.resolutionStatus.replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                        )}

                                        {/* Resolution status change (for warehouse staff) */}
                                        {po.resolutionStatus && po.resolutionStatus !== 'resolved' && !isOperationsEntitled && (
                                            <div className="pt-2 border-t border-slate-100 dark:border-white/5">
                                                <div
                                                    data-testid="po-details-resolution-upgrade-notice"
                                                    className="text-[11px] text-slate-500 dark:text-slate-400"
                                                >
                                                    Advanced exception resolution tracking requires the Operations plan ($199/mo). Standard PO status updates remain available.
                                                </div>
                                            </div>
                                        )}
                                        {po.resolutionStatus && po.resolutionStatus !== 'resolved' && isOperationsEntitled && (
                                            <div className="pt-2 border-t border-slate-100 dark:border-white/5">
                                                <select
                                                    className="text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded px-2 py-1 w-full"
                                                    value={po.resolutionStatus}
                                                    onChange={async (e) => {
                                                        try {
                                                            const res = await fetch(`/api/warehouse-pos/${po.id}/resolution`, {
                                                                method: 'PATCH',
                                                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                                                body: JSON.stringify({ resolutionStatus: e.target.value }),
                                                            });
                                                            if (!res.ok) throw new Error((await res.json()).error);
                                                            onUpdate();
                                                        } catch (err: any) {
                                                            console.error('Resolution update error:', err);
                                                            setError(err.message || 'Failed to update resolution');
                                                        }
                                                    }}
                                                >
                                                    <option value="unresolved">Unresolved</option>
                                                    <option value="followup_created">Follow-up Created</option>
                                                    <option value="supplier_confirmed_missing">Supplier Confirmed Missing</option>
                                                    <option value="resolved">Resolved</option>
                                                    <option value="written_off">Written Off</option>
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            )}
                            {/* Documents Section — PO-owned, pre-arrival */}
                            <div className="border-t border-slate-200 dark:border-white/5 -mx-6 px-6 pt-4 space-y-3">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-500 flex items-center gap-2">
                                    <Paperclip size={13} /> Documents
                                    <span className="text-[10px] bg-slate-100 dark:bg-white/5 text-slate-400 px-1.5 py-0.5 rounded border border-slate-200 dark:border-white/10 font-medium normal-case tracking-normal">Warehouse only</span>
                                </h4>

                                {/* Upload buttons */}
                                <div className="flex flex-wrap gap-2">
                                    {['BOL', 'POD', 'Customs', 'Other'].map((type) => (
                                        <label
                                            key={type}
                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border cursor-pointer transition-colors
                                                ${uploadingPODoc
                                                    ? 'opacity-50 pointer-events-none border-slate-300 dark:border-white/10 text-slate-400'
                                                    : 'border-slate-300 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'}`}
                                        >
                                            {uploadingPODoc
                                                ? <RotateCw size={11} className="animate-spin" />
                                                : <Paperclip size={11} />}
                                            {type}
                                            <input
                                                type="file"
                                                className="hidden"
                                                onChange={(e) => handlePODocUpload(e, type)}
                                                disabled={uploadingPODoc}
                                            />
                                        </label>
                                    ))}
                                </div>

                                {/* Error */}
                                {poDocsError && (
                                    <p className="text-xs text-red-400">{poDocsError}</p>
                                )}

                                {/* Document list */}
                                {poDocsLoading ? (
                                    <p className="text-xs text-muted-foreground">Loading documents…</p>
                                ) : poDocuments.length === 0 ? (
                                    <p className="text-xs text-muted-foreground italic">No documents uploaded yet.</p>
                                ) : (
                                    <div className="space-y-1.5">
                                        {poDocuments.map((doc: any) => (
                                            <div
                                                key={doc.id}
                                                className="flex items-center justify-between py-1.5 px-2.5 rounded-md bg-amber-50/50 dark:bg-amber-500/5 border border-amber-200/50 dark:border-amber-500/20"
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/10 px-1.5 py-0.5 rounded shrink-0">
                                                        {doc.fileType}
                                                    </span>
                                                    <span className="text-xs text-slate-700 dark:text-slate-300 truncate">{doc.fileName}</span>
                                                </div>
                                                <button
                                                    onClick={() => openDocumentView(doc)}
                                                    className="text-[10px] text-blue-500 hover:text-blue-400 ml-2 shrink-0 flex items-center gap-0.5"
                                                >
                                                    <ExternalLink size={10} /> View
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer (Actions) */}
                        <div className="flex-none p-4 border-t border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-slate-900/50 z-10">
                            <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => setShowBOL(true)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-foreground hover:bg-slate-100 dark:hover:bg-white/10 rounded-md transition-colors"
                                >
                                    <Printer size={14} />
                                    Print
                                </button>
                                {/* Edit button — only for non-closed POs */}
                                {po.status !== 'closed' && (
                                    <button
                                        onClick={startEditing}
                                        disabled={loading}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-foreground hover:bg-slate-100 dark:hover:bg-white/10 rounded-md transition-colors disabled:opacity-50"
                                    >
                                        <Pencil size={14} />
                                        Edit
                                    </button>
                                )}
                                <button
                                    onClick={() => setPendingDelete(true)}
                                    disabled={loading}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-md transition-colors disabled:opacity-50"
                                >
                                    <Trash2 size={14} />
                                    Delete
                                </button>
                            </div>

                            <div className="flex items-center gap-2">
                                {/* Hold / Release Hold */}
                                {po.status === 'open' && (
                                    <button
                                        onClick={() => onRequestHold?.(po)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-md transition-colors border border-red-200 dark:border-red-800/50"
                                    >
                                        <AlertCircle size={14} />
                                        Hold
                                    </button>
                                )}

                                {po.status === 'on_hold' && (
                                    <button
                                        onClick={() => onRequestReleaseHold?.(po)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/10 rounded-md transition-colors border border-emerald-200 dark:border-emerald-800/50"
                                    >
                                        <CheckCircle2 size={14} />
                                        Release
                                    </button>
                                )}

                                {/* Complete / Reopen */}
                                {(po.status === 'open' || po.status === 'on_hold') ? (
                                    po.status === 'open' ? (
                                        <button
                                            onClick={() => handleStatusChange('closed')}
                                            disabled={loading}
                                            className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-md transition-all disabled:opacity-50"
                                        >
                                            <CheckCircle2 size={14} />
                                            Complete
                                        </button>
                                    ) : (
                                        <span className="text-xs text-slate-400 italic">
                                            Release hold to complete
                                        </span>
                                    )
                                ) : (
                                    <button
                                        onClick={() => handleStatusChange('open')}
                                        disabled={loading}
                                        className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs font-semibold rounded-md transition-all disabled:opacity-50"
                                    >
                                        <RotateCw size={14} />
                                        Reopen
                                    </button>
                                )}
                            </div>
                            </div>
                        </div>
                    </DialogContent>
                </DialogPortal>
            )}
        </Dialog>

        {/* Delete Confirmation AlertDialog */}
        <AlertDialog open={pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(false); }}>
            <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <AlertDialogHeader>
                    <AlertDialogTitle className="text-slate-900 dark:text-slate-100">Delete Purchase Order</AlertDialogTitle>
                    <AlertDialogDescription className="text-slate-600 dark:text-slate-400">This will permanently delete PO {po?.customerPoNumber}. This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel className="text-slate-700 dark:text-slate-300">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        className="bg-red-600 hover:bg-red-500 text-white"
                        onClick={() => handleDelete()}
                    >
                        Delete
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>

        {/* PO-level BOL Modal */}
        {showBOL && po && (
            <MasterBOLModal
                poData={{
                    id: po.id,
                    customerPoNumber: po.customerPoNumber,
                    clientName: po.clientName,
                    totalSkids: po.totalSkids,
                    type: po.type,
                    description: po.description,
                }}
                onClose={() => setShowBOL(false)}
            />
        )}

        {/* Edit PO Modal */}
        <Dialog open={isEditing} onOpenChange={(isOpen) => { if (!isOpen && !editSaving) setIsEditing(false); }}>
            <DialogPortal>
                <DialogOverlay />
                <DialogContent className="w-[90vw] max-w-[520px] max-h-[85vh] p-0 flex flex-col gap-0">
                    {/* Header */}
                    <div className="flex-none p-5 border-b border-slate-200 dark:border-white/5 bg-white dark:bg-slate-950/95 rounded-t-xl flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Pencil size={18} className="text-blue-500" />
                            <h3 className="text-lg font-semibold text-foreground">Edit Purchase Order</h3>
                        </div>
                        <button
                            onClick={() => setIsEditing(false)}
                            disabled={editSaving}
                            className="p-2 -mr-2 text-slate-400 hover:text-foreground hover:bg-slate-100 dark:hover:bg-white/10 rounded-full transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Form */}
                    <div className="flex-1 overflow-y-auto p-5 space-y-4">
                        {error && (
                            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                                <p className="text-xs text-red-400">{error}</p>
                            </div>
                        )}

                        {/* PO Number */}
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">PO Number <span className="text-red-400">*</span></label>
                            <input
                                type="text"
                                value={editFields.customerPoNumber}
                                onChange={(e) => { setEditFields(f => ({ ...f, customerPoNumber: e.target.value })); setFieldErrors(e => ({ ...e, customerPoNumber: '' })); }}
                                className={`w-full bg-slate-50 dark:bg-slate-950 border rounded-lg px-3 py-2 text-sm text-foreground font-mono focus:outline-none focus:ring-1 transition-colors ${
                                    fieldErrors.customerPoNumber ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-white/10 focus:ring-blue-500'
                                }`}
                                disabled={editSaving}
                            />
                            {fieldErrors.customerPoNumber && <p className="text-xs text-red-400">{fieldErrors.customerPoNumber}</p>}
                        </div>

                        {/* Client Name */}
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Client Name <span className="text-red-400">*</span></label>
                            <input
                                type="text"
                                value={editFields.clientName}
                                onChange={(e) => { setEditFields(f => ({ ...f, clientName: e.target.value })); setFieldErrors(e => ({ ...e, clientName: '' })); }}
                                className={`w-full bg-slate-50 dark:bg-slate-950 border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 transition-colors ${
                                    fieldErrors.clientName ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-white/10 focus:ring-blue-500'
                                }`}
                                disabled={editSaving}
                            />
                            {fieldErrors.clientName && <p className="text-xs text-red-400">{fieldErrors.clientName}</p>}
                        </div>

                        {/* Type */}
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Direction</label>
                            <select
                                value={editFields.type}
                                onChange={(e) => { setEditFields(f => ({ ...f, type: e.target.value as 'inbound' | 'outbound' })); setFieldErrors(e => ({ ...e, type: '' })); }}
                                className={`w-full bg-slate-50 dark:bg-slate-950 border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 transition-colors cursor-pointer ${
                                    fieldErrors.type ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-white/10 focus:ring-blue-500'
                                }`}
                                disabled={editSaving}
                            >
                                <option value="inbound">Inbound</option>
                                <option value="outbound">Outbound</option>
                            </select>
                            {fieldErrors.type && <p className="text-xs text-red-400">{fieldErrors.type}</p>}
                        </div>

                        {/* Skids + Weight row */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Total Skids</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={editFields.totalSkids}
                                    onChange={(e) => { setEditFields(f => ({ ...f, totalSkids: e.target.value })); setFieldErrors(e => ({ ...e, totalSkids: '' })); }}
                                    className={`w-full bg-slate-50 dark:bg-slate-950 border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 transition-colors ${
                                        fieldErrors.totalSkids ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-white/10 focus:ring-blue-500'
                                    }`}
                                    placeholder="0"
                                    disabled={editSaving}
                                />
                                {fieldErrors.totalSkids && <p className="text-xs text-red-400">{fieldErrors.totalSkids}</p>}
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Total Weight (lbs)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={editFields.totalWeight}
                                    onChange={(e) => { setEditFields(f => ({ ...f, totalWeight: e.target.value })); setFieldErrors(e => ({ ...e, totalWeight: '' })); }}
                                    className={`w-full bg-slate-50 dark:bg-slate-950 border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 transition-colors ${
                                        fieldErrors.totalWeight ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-white/10 focus:ring-blue-500'
                                    }`}
                                    placeholder="0"
                                    disabled={editSaving}
                                />
                                {fieldErrors.totalWeight && <p className="text-xs text-red-400">{fieldErrors.totalWeight}</p>}
                            </div>
                        </div>

                        {/* Carrier Email */}
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Carrier Email</label>
                            <input
                                type="email"
                                value={editFields.carrierEmail}
                                onChange={(e) => setEditFields(f => ({ ...f, carrierEmail: e.target.value }))}
                                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                                placeholder="carrier@example.com"
                                disabled={editSaving}
                            />
                        </div>

                        {/* Due Date */}
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Due Date</label>
                            <div className="flex gap-2">
                                <input
                                    type="date"
                                    value={editFields.dueDate}
                                    onChange={(e) => setEditFields(f => ({ ...f, dueDate: e.target.value }))}
                                    className="flex-1 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors [color-scheme:light] dark:[color-scheme:dark]"
                                    disabled={editSaving}
                                />
                                {editFields.dueDate && (
                                    <button
                                        type="button"
                                        onClick={() => setEditFields(f => ({ ...f, dueDate: '' }))}
                                        disabled={editSaving}
                                        className="px-2 py-1 text-xs text-slate-500 hover:text-red-500 transition-colors"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Priority */}
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Priority</label>
                            <select
                                value={editFields.priority}
                                onChange={(e) => setEditFields(f => ({ ...f, priority: e.target.value as any }))}
                                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors cursor-pointer"
                                disabled={editSaving}
                            >
                                <option value="standard">Standard</option>
                                <option value="urgent">⚡ Urgent</option>
                                <option value="critical">🔴 Critical</option>
                            </select>
                        </div>

                        {/* Notes */}
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Notes</label>
                            <textarea
                                value={editFields.description}
                                onChange={(e) => setEditFields(f => ({ ...f, description: e.target.value }))}
                                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors min-h-[80px] resize-y"
                                placeholder="Additional notes..."
                                disabled={editSaving}
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex-none p-5 border-t border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-end gap-3">
                        <button
                            onClick={() => setIsEditing(false)}
                            disabled={editSaving}
                            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-foreground bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleEditSave}
                            disabled={editSaving || !editFields.customerPoNumber.trim() || !editFields.clientName.trim()}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg shadow-lg shadow-blue-900/20 transition-all disabled:opacity-50"
                        >
                            {editSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={16} />}
                            Save Changes
                        </button>
                    </div>
                </DialogContent>
            </DialogPortal>
        </Dialog>
        </>
    );
}
